/**
 * Server Time Sync Utility
 * Prevents time manipulation by always using server time as source of truth
 */

const SERVER_TIME_KEY = '_sst';
const LAST_SYNC_KEY = '_sst_sync';

/**
 * Format Date to MySQL datetime format (YYYY-MM-DD HH:MM:SS)
 * This matches the format returned by the GET API and expected by POST API
 */
export function formatToMySQLDateTime(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Get current server time by adding elapsed time to stored server time
 * This ensures we use server time even when offline
 */
export function getCurrentServerTime(): Date {
    const storedTime = localStorage.getItem(SERVER_TIME_KEY);
    const lastSyncTime = localStorage.getItem(LAST_SYNC_KEY);
    
    if (!storedTime || !lastSyncTime) {
        // Fallback to system time if no server time stored
        return new Date();
    }
    
    try {
        const serverTimeAtSync = new Date(storedTime);
        const syncTimestamp = parseInt(lastSyncTime);
        const elapsedMs = Date.now() - syncTimestamp;
        
        // Add elapsed time to stored server time
        return new Date(serverTimeAtSync.getTime() + elapsedMs);
    } catch {
        return new Date();
    }
}

/**
 * Update stored server time (called on every API response)
 */
export function updateServerTime(serverTimeString: string): void {
    try {
        localStorage.setItem(SERVER_TIME_KEY, serverTimeString);
        localStorage.setItem(LAST_SYNC_KEY, Date.now().toString());
    } catch (error) {
        console.error('Failed to update server time:', error);
    }
}

/**
 * Calculate time difference in milliseconds between current server time and target time
 */
export function calculateTimeDiff(targetTimeString: string): number {
    if (!targetTimeString) return 0;
    
    try {
        const currentServerTime = getCurrentServerTime();
        const targetTime = new Date(targetTimeString);
        return targetTime.getTime() - currentServerTime.getTime();
    } catch {
        return 0;
    }
}

/**
 * Format time difference as countdown string (e.g., "23h 45m 12s")
 */
export function formatCountdown(milliseconds: number): string {
    if (milliseconds <= 0) return '';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    
    if (hours > 0) {
        return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    } else {
        return `${seconds}s`;
    }
}

/**
 * Add hours to a date string and return new ISO string
 */
export function addHoursToDate(dateString: string, hours: number): string {
    try {
        const date = new Date(dateString);
        date.setHours(date.getHours() + hours);
        return date.toISOString();
    } catch {
        return '';
    }
}

/**
 * Check if current server time is after target time
 */
export function isAfterTime(targetTimeString: string): boolean {
    if (!targetTimeString) return true;
    return calculateTimeDiff(targetTimeString) <= 0;
}

/**
 * Check if current server time is before target time
 */
export function isBeforeTime(targetTimeString: string): boolean {
    if (!targetTimeString) return false;
    return calculateTimeDiff(targetTimeString) > 0;
}
